import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, CircleHelp, EyeOff, MessageSquarePlus, UserPlus } from "lucide-react";
import {
  type ComponentType,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router";
import { Draggable } from "@/components/draggable";
import { FeedbackDialog } from "@/components/feedback-dialog";
import { InviteDialog } from "@/components/invite-dialog";
import {
  HELP_SEEN_AFTER_MS,
  helpNeedsAttention,
  markHelpSeen,
  onHelpAttention,
} from "@/lib/help-attention";
import { type MenuPlacement, menuPlacement } from "@/lib/menu-placement";
import { meQuery } from "@/lib/queries";
import { useWebsiteUrl } from "@/lib/site";
import { cn } from "@/lib/utils";

const HIDDEN_KEY = "gridaction.float.hidden";

// What the round button shrinks to (size-13, or size-11 on a short screen), for placing the menu.
const SETTLED_SIZE = 52;
const SETTLED_SIZE_SHORT = 44;

function wasHidden(): boolean {
  try {
    return localStorage.getItem(HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}

const MENU_ITEM =
  "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left font-medium text-foreground text-sm hover:bg-surface-2";

type MenuIcon = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

function MenuItem({
  icon: Icon,
  onClick,
  children,
}: {
  icon: MenuIcon;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} className={MENU_ITEM}>
      <Icon className="size-4 text-muted" aria-hidden />
      {children}
    </button>
  );
}

/** A menu entry that goes to another page, closing the menu as it goes. A whole address (the website's) is an ordinary link. */
function MenuLink({
  icon: Icon,
  to,
  onClick,
  children,
}: {
  icon: MenuIcon;
  to: string;
  onClick: () => void;
  children: ReactNode;
}) {
  const inside = (
    <>
      <Icon className="size-4 text-muted" aria-hidden />
      {children}
    </>
  );
  return /^https?:\/\//.test(to) ? (
    <a href={to} onClick={onClick} className={MENU_ITEM}>
      {inside}
    </a>
  ) : (
    <Link to={to} onClick={onClick} className={MENU_ITEM}>
      {inside}
    </Link>
  );
}

/** The coach's clipboard, with a play drawn on it. */
function PlayClipboard({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="4.6" y="4.4" width="14.8" height="17.4" rx="2.2" />
      <rect x="8.6" y="2.2" width="6.8" height="4" rx="1.2" fill="currentColor" fillOpacity="0.2" />
      <path d="M8 10.2l3 3m0-3l-3 3" />
      <circle cx="15.2" cy="16.8" r="1.9" />
      <path d="M8.4 18.2c2.6-.2 4.2-1.6 4.8-3.8" strokeDasharray="1.6 1.8" />
    </svg>
  );
}

/**
 * One round Help button on every page except the home page for someone who has no account (see
 * `FloatingActions`), a clipboard on a plain surface with a border. It opens a
 * small menu: the Help and FAQ page (left out on that page itself), send feedback, invite friends (signed-in people only, since the credit goes to an
 * account) and hide the button. It is a big gold circle with a pulsing ring the first time it is
 * seen and after each sign-in or sign-out, and fades to a smaller pale-gold one (solid again on
 * hover) once it has been on screen a few seconds or opened. It can be dragged anywhere with a
 * finger and tucks away behind a small tab. It sits above the phone's bottom bar to start with.
 */
function HelpButton() {
  const { data: me } = useQuery({ ...meQuery, retry: false });
  const { pathname } = useLocation();
  const site = useWebsiteUrl();
  const [hidden, setHidden] = useState(wasHidden);
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [big, setBig] = useState(helpNeedsAttention);
  const [menuAt, setMenuAt] = useState<MenuPlacement | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const openedBig = useRef(false);

  // A sign-in asks for the big button again.
  useEffect(() => onHelpAttention(() => setBig(true)), []);

  // Big until it has been on screen a few seconds, then small for good (until the next sign-in).
  useEffect(() => {
    if (!big || hidden) return;
    const timer = window.setTimeout(() => {
      markHelpSeen();
      setBig(false);
    }, HELP_SEEN_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [big, hidden]);

  useEffect(() => {
    if (!menuOpen) return;
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    // A turned phone or resized window moves things around; close rather than leave the menu off screen.
    const close = () => setMenuOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", onEscape);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", onEscape);
      window.removeEventListener("resize", close);
    };
  }, [menuOpen]);

  // Wherever the button has been dragged, put the menu where there is room for it. It is measured
  // once it is on the page and moved before the screen is painted, so it never flashes in the
  // wrong place. A big button is about to shrink, so the menu is placed for its small size.
  useLayoutEffect(() => {
    const list = menu.current;
    const corner = box.current?.getBoundingClientRect();
    if (!menuOpen || !list || !corner) return;
    const short = window.matchMedia?.("(max-height: 500px)").matches;
    const settled = openedBig.current ? (short ? SETTLED_SIZE_SHORT : SETTLED_SIZE) : corner.height;
    setMenuAt(
      menuPlacement(
        corner,
        settled,
        { width: list.offsetWidth, height: list.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [menuOpen]);

  const toggleMenu = () => {
    openedBig.current = big;
    if (big) {
      markHelpSeen();
      setBig(false);
    }
    setMenuOpen(!menuOpen);
  };

  const setAway = (away: boolean) => {
    setHidden(away);
    setMenuOpen(false);
    try {
      localStorage.setItem(HIDDEN_KEY, away ? "1" : "0");
    } catch {
      // Not remembering is fine.
    }
  };

  // z-50, level with the sign-in chip at the top right but later on the page, so a button dragged
  // under the chip is still on top and can be reached. Dialogs are also z-50 and come later still,
  // so they stay above it.
  const edge =
    "fixed right-0 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-50 flex flex-col items-end gap-2 md:bottom-6 [@media(max-height:500px)]:bottom-3 print:hidden";

  if (hidden) {
    return (
      <div className={edge}>
        <Draggable id="tab">
          <button
            type="button"
            className="grid h-10 w-7 place-items-center rounded-l-xl border border-r-0 border-border bg-surface text-muted shadow-md"
            aria-label="Show the Help button"
            onClick={() => setAway(false)}
          >
            <ChevronLeft className="size-4" aria-hidden />
          </button>
        </Draggable>
      </div>
    );
  }

  return (
    <div className={`${edge} pr-[max(1rem,env(safe-area-inset-right))]`}>
      {/* Dragging the button away from where the menu was placed would leave it in the wrong spot, so it closes. */}
      <Draggable id="help" onDragStart={() => setMenuOpen(false)}>
        <div ref={box} className="relative">
          {menuOpen ? (
            <div
              ref={menu}
              id="help-menu"
              style={menuAt ?? undefined}
              className={cn(
                "absolute w-52 max-w-[calc(100vw-2rem)] rounded-2xl border border-border bg-surface p-1.5 shadow-xl",
                !menuAt && "right-0 bottom-full mb-2",
              )}
            >
              {pathname === "/help" && !site ? null : (
                <MenuLink
                  icon={CircleHelp}
                  to={site ? `${site}/help/` : "/help"}
                  onClick={() => setMenuOpen(false)}
                >
                  Help and FAQ
                </MenuLink>
              )}
              <MenuItem
                icon={MessageSquarePlus}
                onClick={() => {
                  setMenuOpen(false);
                  setFeedbackOpen(true);
                }}
              >
                Send feedback
              </MenuItem>
              {me ? (
                <MenuItem
                  icon={UserPlus}
                  onClick={() => {
                    setMenuOpen(false);
                    setInviteOpen(true);
                  }}
                >
                  Invite friends
                </MenuItem>
              ) : null}
              <MenuItem icon={EyeOff} onClick={() => setAway(true)}>
                Hide this button
              </MenuItem>
            </div>
          ) : null}
          {big ? (
            // A gold ring that pulses outward while the button is new (a still halo with reduced motion).
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full border-4 border-gold-strong motion-safe:animate-ping motion-reduce:scale-125 motion-reduce:opacity-70"
            />
          ) : null}
          <button
            type="button"
            aria-label="Help, feedback and invites"
            aria-expanded={menuOpen}
            aria-controls="help-menu"
            onClick={toggleMenu}
            className={cn(
              "relative grid place-items-center rounded-full border-2 shadow-lg transition-[width,height,opacity,background-color,color,border-color] duration-500 motion-reduce:transition-none",
              big
                ? "size-18 border-board bg-gold-strong text-board opacity-100 [@media(max-height:500px)]:size-14"
                : "size-13 border-gold-strong bg-gold-bg text-foreground opacity-85 hover:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100 [@media(max-height:500px)]:size-11",
            )}
          >
            <PlayClipboard
              className={
                big
                  ? "size-10 [@media(max-height:500px)]:size-8"
                  : "size-7 [@media(max-height:500px)]:size-6"
              }
            />
          </button>
        </div>
      </Draggable>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      {me ? <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} /> : null}
    </div>
  );
}

/**
 * The Help button, except where it would only be in the way: someone landing on the home page
 * without an account has nothing to report and nobody to invite yet, so the page greets them
 * without it. An account brings it back there, and every other page shows it as before. It is not
 * mounted at all while hidden, so its "big the first time" look is kept for the first page that
 * does show it, and nothing flashes in while the account check is still answering.
 */
export function FloatingActions() {
  const { data: me } = useQuery({ ...meQuery, retry: false });
  const { pathname } = useLocation();
  if (pathname === "/" && !me) return null;
  return <HelpButton />;
}
